'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, Package, ShoppingCart, Warehouse, Radio, Users2, Megaphone, DollarSign, Wallet, MessageSquare, ShieldCheck, Settings, BarChart3, Bell, HelpCircle, LogOut, ChevronLeft, ChevronRight, ChevronDown,  } from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href?: string;
  badge?: number;
  badgeVariant?: 'danger' | 'warning' | 'info';
  children?: { label: string; href: string }[];
}

const navItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboard size={18} />,
    href: '/',
  },
  {
    id: 'products',
    label: 'Products',
    icon: <Package size={18} />,
    href: '/product-management',
    children: [
      { label: 'All Products', href: '/product-management' },
      { label: 'Add Product', href: '/product-management?action=add' },
      { label: 'Import CSV', href: '/product-management?action=import' },
      { label: 'Inventory', href: '/product-management?tab=inventory' },
      { label: 'Low Stock Alerts', href: '/product-management?tab=lowstock' },
    ],
  },
  {
    id: 'orders',
    label: 'Orders',
    icon: <ShoppingCart size={18} />,
    href: '/order-management',
    badge: 14,
    badgeVariant: 'danger',
    children: [
      { label: 'All Orders', href: '/order-management' },
      { label: 'Pending', href: '/order-management?status=pending' },
      { label: 'Processing', href: '/order-management?status=processing' },
      { label: 'Shipped', href: '/order-management?status=shipped' },
      { label: 'Return / Refund', href: '/order-management?status=return' },
      { label: 'Disputes', href: '/order-management?status=dispute' },
    ],
  },
  {
    id: 'fulfillment',
    label: 'Fulfillment / FBA',
    icon: <Warehouse size={18} />,
    badge: 3,
    badgeVariant: 'info',
    children: [
      { label: 'FBA Inventory', href: '#' },
      { label: 'Send to FC', href: '#' },
      { label: 'FBA Orders', href: '#' },
      { label: 'FBA Shipments', href: '#' },
      { label: 'Warehouse List', href: '#' },
    ],
  },
  {
    id: 'livestream',
    label: 'LIVE Streams',
    icon: <Radio size={18} />,
    children: [
      { label: 'My Streams', href: '#' },
      { label: 'Start New Stream', href: '#' },
      { label: 'Scheduled', href: '#' },
      { label: 'Replays', href: '#' },
      { label: 'Chat Settings', href: '#' },
    ],
  },
  {
    id: 'groupbuying',
    label: 'Group Buying',
    icon: <Users2 size={18} />,
    badge: 2,
    badgeVariant: 'warning',
    children: [
      { label: 'My Deals', href: '#' },
      { label: 'Create Deal', href: '#' },
      { label: 'Active Groups', href: '#' },
      { label: 'Completed', href: '#' },
    ],
  },
  {
    id: 'promotions',
    label: 'Promotions & Ads',
    icon: <Megaphone size={18} />,
    children: [
      { label: 'My Coupons', href: '#' },
      { label: 'Flash Sales', href: '#' },
      { label: 'Sponsored Products', href: '#' },
      { label: 'Ad Campaigns', href: '#' },
      { label: 'Ad Analytics', href: '#' },
    ],
  },
  {
    id: 'earnings',
    label: 'Earnings & Payouts',
    icon: <DollarSign size={18} />,
    children: [
      { label: 'Summary', href: '#' },
      { label: 'Transactions', href: '#' },
      { label: 'Withdraw', href: '#' },
      { label: 'Payout History', href: '#' },
      { label: 'Tax Invoice', href: '#' },
    ],
  },
  {
    id: 'wallet',
    label: 'Wallet & Virtual Card',
    icon: <Wallet size={18} />,
    children: [
      { label: 'Balance', href: '#' },
      { label: 'Add Money', href: '#' },
      { label: 'Virtual Card', href: '#' },
      { label: 'Card Transactions', href: '#' },
      { label: 'Tokens', href: '#' },
    ],
  },
  {
    id: 'communication',
    label: 'Communication',
    icon: <MessageSquare size={18} />,
    badge: 7,
    badgeVariant: 'danger',
    children: [
      { label: 'Q&A', href: '#' },
      { label: 'Live Chat', href: '#' },
      { label: 'Reviews', href: '#' },
      { label: 'Bulk Email', href: '#' },
    ],
  },
  {
    id: 'health',
    label: 'Account Health',
    icon: <ShieldCheck size={18} />,
    badge: 1,
    badgeVariant: 'warning',
    children: [
      { label: 'Health Score', href: '#' },
      { label: 'Performance', href: '#' },
      { label: 'Violations', href: '#' },
      { label: 'Appeals', href: '#' },
    ],
  },
  {
    id: 'store-settings',
    label: 'Store Settings',
    icon: <Settings size={18} />,
    children: [
      { label: 'Store Profile', href: '#' },
      { label: 'Business Info', href: '#' },
      { label: 'Documents', href: '#' },
      { label: 'Payment Methods', href: '#' },
      { label: 'Shipping', href: '#' },
      { label: 'Return Policy', href: '#' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: <BarChart3 size={18} />,
    children: [
      { label: 'Sales Report', href: '#' },
      { label: 'Product Performance', href: '#' },
      { label: 'Customer Report', href: '#' },
      { label: 'Inventory Report', href: '#' },
      { label: 'Tax Report', href: '#' },
      { label: 'Payout Report', href: '#' },
    ],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: <Bell size={18} />,
    badge: 12,
    badgeVariant: 'danger',
    children: [
      { label: 'All Notifications', href: '#' },
      { label: 'Settings', href: '#' },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    icon: <HelpCircle size={18} />,
    children: [
      { label: 'Contact Admin', href: '#' },
      { label: 'Help Center', href: '#' },
      { label: 'Training Videos', href: '#' },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const [expandedItems, setExpandedItems] = useState<string[]>(['products', 'orders']);

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const isActive = (item: NavItem): boolean => {
    if (item.href && item.href !== '#') {
      if (item.href === '/') return pathname === '/';
      return pathname.startsWith(item.href);
    }
    if (item.children) {
      return item.children.some(
        (c) => c.href !== '#' && pathname.startsWith(c.href)
      );
    }
    return false;
  };

  const getBadgeClass = (variant?: string) => {
    if (variant === 'danger') return 'bg-danger text-white';
    if (variant === 'warning') return 'bg-warning text-white';
    if (variant === 'info') return 'bg-info text-white';
    return 'bg-muted text-muted-foreground';
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-card border-r border-border flex flex-col z-30 sidebar-transition shadow-sidebar ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div
        className={`flex items-center border-b border-border shrink-0 ${
          collapsed ? 'justify-center px-0 py-4 h-16' : 'gap-2 px-4 py-4 h-16'
        }`}
      >
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-bold text-base text-primary tracking-tight">
            Arain7Seller
          </span>
        )}
      </div>

      {/* Nav Items */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2">
        {navItems.map((item) => {
          const active = isActive(item);
          const expanded = expandedItems.includes(item.id);
          const hasChildren = item.children && item.children.length > 0;

          return (
            <div key={`nav-${item.id}`} className="mb-0.5">
              {/* Main nav item */}
              {item.href && !hasChildren ? (
                <Link
                  href={item.href}
                  className={active ? 'nav-item-active' : 'nav-item'}
                  title={collapsed ? item.label : undefined}
                >
                  <span className="shrink-0">{item.icon}</span>
                  {!collapsed && (
                    <>
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge && (
                        <span
                          className={`text-2xs font-bold px-1.5 py-0.5 rounded-full ${getBadgeClass(item.badgeVariant)}`}
                        >
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                  {collapsed && item.badge && (
                    <span
                      className={`absolute left-8 top-1 text-2xs font-bold px-1 py-0.5 rounded-full ${getBadgeClass(item.badgeVariant)}`}
                    >
                      {item.badge}
                    </span>
                  )}
                </Link>
              ) : (
                <button
                  onClick={() => !collapsed && toggleExpand(item.id)}
                  className={`w-full ${active ? 'nav-item-active' : 'nav-item'} ${collapsed ? 'justify-center' : ''} relative`}
                  title={collapsed ? item.label : undefined}
                >
                  <span className="shrink-0">{item.icon}</span>
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left truncate">{item.label}</span>
                      {item.badge && (
                        <span
                          className={`text-2xs font-bold px-1.5 py-0.5 rounded-full ${getBadgeClass(item.badgeVariant)}`}
                        >
                          {item.badge}
                        </span>
                      )}
                      <ChevronDown
                        size={14}
                        className={`shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                      />
                    </>
                  )}
                  {collapsed && item.badge && (
                    <span
                      className={`absolute left-7 top-0.5 text-2xs font-bold px-1 py-0.5 rounded-full ${getBadgeClass(item.badgeVariant)}`}
                    >
                      {item.badge}
                    </span>
                  )}
                </button>
              )}

              {/* Children */}
              {!collapsed && hasChildren && expanded && (
                <div className="ml-4 mt-0.5 border-l border-border pl-3 space-y-0.5">
                  {item.children!.map((child) => (
                    <Link
                      key={`nav-${item.id}-${child.label.replace(/\s+/g, '-').toLowerCase()}`}
                      href={child.href}
                      className={`block px-2 py-1.5 rounded-md text-xs font-medium transition-colors duration-100 ${
                        pathname === child.href
                          ? 'text-primary bg-secondary' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Bottom: Logout + Collapse */}
      <div className="shrink-0 border-t border-border p-2 space-y-1">
        <button
          className="nav-item w-full"
          title={collapsed ? 'Logout' : undefined}
        >
          <LogOut size={18} className="shrink-0 text-danger" />
          {!collapsed && (
            <span className="text-danger font-medium">Logout</span>
          )}
        </button>

        <button
          onClick={onToggle}
          className="nav-item w-full justify-center"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : (
            <>
              <ChevronLeft size={16} />
              <span className="text-xs">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}